import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAllergen,
  createMenuSection,
  deleteAllergen,
  deleteMenuSection,
  fetchAllergens,
  fetchMenuSections,
  updateAllergen,
  updateMenuSection,
  type CreateAllergenPayload,
  type CreateMenuSectionPayload,
  type ListAllergensQuery,
  type ListMenuSectionsQuery,
  type UpdateAllergenPayload,
  type UpdateMenuSectionPayload,
} from '@/services/api/retailAttributes.api';

// ─── Allergens ──────────────────────────────────────────────────────────────

export function useAllergens(query: ListAllergensQuery, enabled = true) {
  return useQuery({
    queryKey: ['retail-allergens', query],
    queryFn: () => fetchAllergens(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useCreateAllergen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAllergenPayload) => createAllergen(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-allergens'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
    },
  });
}

export function useUpdateAllergen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAllergenPayload }) =>
      updateAllergen(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-allergens'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
    },
  });
}

export function useDeleteAllergen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAllergen(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-allergens'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
    },
  });
}

// ─── Menu sections ────────────────────────────────────────────────────────────

export function useMenuSections(query: ListMenuSectionsQuery, enabled = true) {
  return useQuery({
    queryKey: ['retail-menu-sections', query],
    queryFn: () => fetchMenuSections(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useCreateMenuSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMenuSectionPayload) => createMenuSection(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-menu-sections'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
    },
  });
}

export function useUpdateMenuSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMenuSectionPayload }) =>
      updateMenuSection(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-menu-sections'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
    },
  });
}

export function useDeleteMenuSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMenuSection(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['retail-menu-sections'] });
      void queryClient.invalidateQueries({ queryKey: ['retail-products'] });
    },
  });
}
